/**
 * One item row on the order detail screen. Recursive: an `additional_service`
 * item renders nested under its parent (never as a sibling — CLAUDE.md and
 * the design brief are both explicit about this), one level deep, because
 * that is the only nesting depth the API produces (additional services are
 * never themselves parents).
 *
 * ⛔ The progress line is derived from `steps`, NEVER from `item.status` — see
 * the ⛔ note at the top of `api/serviceOrders.ts`. `product` items have no
 * steps (`deriveItemProgress` returns `'none'`) and render no progress line.
 */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { deriveItemProgress, type ServiceOrderItem } from '../api/serviceOrders.ts';
import { formatMoney } from '../i18n/format.ts';
import { currentLocale } from '../i18n/index.ts';
import { buildPath } from '../i18n/routes.ts';
import styles from './ServiceOrderItemRow.module.css';
import { MONEY_VISIBLE } from '../money-visibility.ts';

interface Props {
  item: ServiceOrderItem;
  childItems: ServiceOrderItem[];
  currency: string;
  nested?: boolean;
}

/** `deriveItemProgress`'s wire-shaped values, mapped to the catalogue's camelCase keys. */
const PROGRESS_KEY: Record<Exclude<ReturnType<typeof deriveItemProgress>, 'none'>, string> = {
  not_started: 'itemProgress.notStarted',
  in_progress: 'itemProgress.inProgress',
  completed: 'itemProgress.completed',
};

function describeItem(item: ServiceOrderItem, t: (key: string) => string): string {
  // "Item with an empty description — fall back to the service name"
  // (design brief). The payload has no separate service-name field, so the
  // best available fallback is a generic label for the item's own type.
  if (item.description.trim() !== '') return item.description;
  return t(`item.fallback.${item.type}`);
}

function ReportBadge({
  label,
  confirmed,
  to,
}: {
  label: string;
  confirmed: boolean;
  to: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  // The whole badge navigates to the laudo viewer (LaudoPage) — a pending
  // laudo is still a page: it renders its own honest "being finalised" state.
  return (
    <Link
      to={to}
      className={`${styles['report']} ${confirmed ? styles['reportConfirmed'] : styles['reportPending']}`}
    >
      {label}: {t(confirmed ? 'report.confirmed' : 'report.pending')}
    </Link>
  );
}

export function ServiceOrderItemRow({ item, childItems, currency, nested = false }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const progress = deriveItemProgress(item.steps);
  // This row only ever renders inside the order detail route, so `osId` is the
  // order in the address bar — the same order the item belongs to.
  const { osId } = useParams();
  const laudoPath = (tipo: 'entry' | 'exit'): string =>
    buildPath('laudo', currentLocale(), { osId, itemId: String(item.item_id), tipo });

  return (
    <li className={`${styles['row']} ${nested ? styles['nested'] : ''}`}>
      <div className={styles['main']}>
        <div className={styles['titleLine']}>
          <span className={styles['description']}>{describeItem(item, t)}</span>
          <span className={styles['qty']}>{t('item.quantityTimes', { count: item.quantity })}</span>
          {/* see money-visibility.ts */}
          {MONEY_VISIBLE && (
            <span className={styles['price']}>{formatMoney({ amount: item.total_cents, currency })}</span>
          )}
        </div>

        {progress !== 'none' && <p className={styles['progress']}>{t(PROGRESS_KEY[progress])}</p>}

        {(item.reports?.entry || item.reports?.exit) && (
          <div className={styles['reports']}>
            {item.reports?.entry && (
              <ReportBadge label={t('report.entry')} confirmed={item.reports.entry.confirmed} to={laudoPath('entry')} />
            )}
            {item.reports?.exit && (
              <ReportBadge label={t('report.exit')} confirmed={item.reports.exit.confirmed} to={laudoPath('exit')} />
            )}
          </div>
        )}
      </div>

      {childItems.length > 0 && (
        <ul className={styles['children']}>
          {childItems.map((child) => (
            <ServiceOrderItemRow key={child.item_id} item={child} childItems={[]} currency={currency} nested />
          ))}
        </ul>
      )}
    </li>
  );
}
